/**
 * ListView: plantilla estándar para listas admin (Page + filtros/tabs + IndexTable + EmptyState + bulk actions).
 *
 * PLANTILLA OBLIGATORIA: Todas las páginas que usan este componente deben cumplir el checklist completo
 * (presets al clic en lupa, búsqueda, filtros, ordenar, editar columnas, contentBefore, acciones solo iconos).
 * Ver docs/guides/LISTA-ESTANDAR-PLANTILLA.md → "Checklist obligatorio: plantilla ListView completa".
 * Regla Cursor: .cursor/rules/listview-full-template.mdc
 *
 * Sin imports directos de @shopify/polaris — usa wrappers en src/components/admin y src/components/ui.
 * Para migrar de Polaris a otra librería en el futuro: cambiar solo los wrappers, no este archivo.
 */

import React, { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import type { ReactNode } from 'react';
// Polaris — accedido solo via wrappers; estos imports quedan aquí porque son primitivos de layout/tipografía
// que no justifican un wrapper propio (Box, Text, InlineStack, BlockStack, Icon, Badge, Tooltip, Thumbnail,
// Button, Card, Select, Pagination). Los componentes estructurales (Page, IndexTable, EmptyState, Spinner,
// Tabs, Popover) ya están encapsulados en wrappers.
import {
  Card,
  Box,
  Text,
  InlineStack,
  Button,
  Thumbnail,
  Avatar,
  Badge,
  Tooltip,
  Pagination,
  Select,
  BlockStack,
  TextField,
  Icon,
  SkeletonBodyText,
} from '@/lib/polaris';
import { EditIcon, DeleteIcon, ImageIcon, ChevronDownIcon, ChevronRightIcon, PlusIcon, ViewIcon, HideIcon } from '@/lib/polaris-icons';
// Wrappers — sustituyen los imports directos de @shopify/polaris para los componentes estructurales
import { AppPage } from '@/components/admin/AppPage';
import { AppTable } from '@/components/admin/AppTable';
import { AppEmptyState } from '@/components/admin/AppEmptyState';
import { AppConfirmModal } from '@/components/admin/AppConfirmModal';
import { useStoreNavigate } from '@/hooks/useStoreNavigate';
import { useAdminToastOptional } from '@/contexts/AdminToastContext';
import { Spinner } from '@/components/ui/spinner';
import { Tabs } from '@/components/ui/tabs';
import { Popover, PopoverContent } from '@/components/ui/popover';
import type { ExpandableContentRenderer } from '@/types/listView.types';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  horizontalListSortingStrategy,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ListViewConfig, ListViewColumn, ListViewColumnText, ListViewEmptyState, ListViewBulkDelete } from '@/types/listView.types';
import { PageBreadcrumbTitle } from '@/components/polaris/PageBreadcrumbTitle';
import type { UsePaginationReturn } from '@smarty/shared';

/** Ancho máximo por defecto de columnas (texto truncado con ellipsis). Aplica a todas las columnas de contenido texto salvo que la columna defina maxWidth. */
const DEFAULT_COLUMN_MAX_WIDTH = 280;

function wrapCellWithMaxWidth(content: ReactNode, maxWidth: number, title?: string): ReactNode {
  return (
    <span className="ListView-cellTruncate" title={title} style={{ maxWidth }}>
      {content}
    </span>
  );
}

/** Celda expandible estilo Shopify: hover gris + chevron + popover al clic con contenido definido por la config. */
function ExpandableCellWrapper(props: {
  children: ReactNode;
  item: Record<string, unknown>;
  expandableContent: ExpandableContentRenderer;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const { children, item, expandableContent, isOpen, onToggle, onClose } = props;
  return (
    <Popover
      active={isOpen}
      activator={
        <Box
          as="div"
          padding="100"
          paddingInlineEnd="200"
          minWidth="0"
          style={{
            cursor: 'pointer',
            borderRadius: 'var(--p-border-radius-200, 8px)',
            backgroundColor: hovered || isOpen ? 'var(--p-color-bg-surface-hover, #f1f2f4)' : undefined,
          }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
        >
          <InlineStack gap="100" blockAlign="center" wrap={false}>
            {children}
            <Box
              minWidth="0"
              style={{
                opacity: hovered || isOpen ? 1 : 0,
                transition: 'opacity 0.15s ease',
              }}
            >
              <Icon source={ChevronDownIcon} tone="subdued" />
            </Box>
          </InlineStack>
        </Box>
      }
      onClose={onClose}
      preferredPosition="below"
      preferredAlignment="left"
      autofocusTarget="none"
    >
      <PopoverContent>
        {/* stopPropagation: los clics DENTRO del popover (Ver cliente, link a producto)
            no deben disparar el onClick de la fila (que navega al pedido). */}
        <div onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
          {expandableContent(item)}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Barra de scroll horizontal "pegajosa" (estilo Shopify): replica el scroll del
 * contenedor de la tabla en una barra fija al fondo del viewport, sincronizada en
 * ambos sentidos. Así se puede desplazar horizontalmente sin bajar hasta el final
 * de listas largas (el scrollbar nativo del contenedor se oculta por CSS). Solo se
 * muestra cuando la tabla realmente desborda en ancho.
 */
function StickyHorizontalScrollbar({ targetRef }: { targetRef: React.RefObject<HTMLDivElement> }) {
  const barRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [overflowing, setOverflowing] = useState(false);

  useEffect(() => {
    const target = targetRef.current;
    const bar = barRef.current;
    const inner = innerRef.current;
    if (!target || !bar || !inner) return;
    // Alto de la barra = alto exacto del scrollbar nativo del SO (mínimo 12px para
    // sistemas con scrollbars de overlay, p. ej. macOS, donde mide 0).
    const probe = document.createElement('div');
    probe.style.cssText = 'overflow:scroll;width:40px;height:40px;position:absolute;top:-9999px;visibility:hidden';
    document.body.appendChild(probe);
    const scrollbarH = probe.offsetHeight - probe.clientHeight;
    document.body.removeChild(probe);
    bar.style.height = `${Math.max(scrollbarH, 12)}px`;
    let lock = false;
    const measure = () => {
      inner.style.width = `${target.scrollWidth}px`;
      setOverflowing(target.scrollWidth - target.clientWidth > 1);
    };
    const onTarget = () => { if (lock) return; lock = true; bar.scrollLeft = target.scrollLeft; lock = false; };
    const onBar = () => { if (lock) return; lock = true; target.scrollLeft = bar.scrollLeft; lock = false; };
    measure();
    target.addEventListener('scroll', onTarget, { passive: true });
    bar.addEventListener('scroll', onBar, { passive: true });
    const ro = new ResizeObserver(measure);
    ro.observe(target);
    if (target.firstElementChild) ro.observe(target.firstElementChild);
    return () => {
      target.removeEventListener('scroll', onTarget);
      bar.removeEventListener('scroll', onBar);
      ro.disconnect();
    };
  }, [targetRef]);

  return (
    <div
      ref={barRef}
      className="ListView-stickyScrollbar"
      aria-hidden="true"
      style={{ display: overflowing ? 'block' : 'none' }}
    >
      <div ref={innerRef} style={{ height: 1 }} />
    </div>
  );
}

function hasExpandableContent(col: ListViewColumn): col is ListViewColumn & { expandableContent: ExpandableContentRenderer } {
  return 'expandableContent' in col && typeof (col as { expandableContent?: ExpandableContentRenderer }).expandableContent === 'function';
}

export interface ListViewTabsOption {
  tabs: { id: string; content: string }[];
  selected: number;
  onSelect: (index: number) => void;
}

/** Tamaño de página por defecto para listas (debe coincidir con usePagination en shared). */
export const DEFAULT_LIST_PAGE_SIZE = 25;

/** Opciones del selector de tamaño de página en la plantilla. Única fuente de verdad. */
export const LIST_PAGE_SIZE_OPTIONS: { label: string; value: string }[] = [
  { label: '25 por página', value: '25' },
  { label: '50 por página', value: '50' },
  { label: '100 por página', value: '100' },
];

export interface ListViewPaginationOption {
  from: number;
  to: number;
  total: number;
  pageSize: number;
  /** Si no se pasa, la plantilla usa LIST_PAGE_SIZE_OPTIONS. */
  pageSizeOptions?: { label: string; value: string }[];
  totalPages: number;
  hasPrevious: boolean;
  hasNext: boolean;
  onPageSizeChange: (size: number) => void;
  onPrevious: () => void;
  onNext: () => void;
  label?: string;
}

/**
 * Construye el objeto de paginación para ListView desde el hook usePagination.
 * Centraliza opciones y etiquetas; las páginas solo usan usePagination() y este helper.
 */
export function buildListViewPagination(
  pagination: UsePaginationReturn
): ListViewPaginationOption | undefined {
  const total = pagination.total ?? 0;
  if (total <= 0) return undefined;
  return {
    from: pagination.range.from,
    to: pagination.range.to,
    total,
    pageSize: pagination.pageSize,
    pageSizeOptions: LIST_PAGE_SIZE_OPTIONS,
    totalPages: pagination.totalPages ?? 1,
    hasPrevious: pagination.hasPreviousPage,
    hasNext: pagination.hasNextPage,
    onPageSizeChange: pagination.setPageSize,
    onPrevious: pagination.previousPage,
    onNext: pagination.nextPage,
    label: `Página ${pagination.page} de ${pagination.totalPages ?? 1}`,
  };
}

/**
 * Celda de imagen robusta: muestra la miniatura y, si la URL falta o falla al cargar
 * (imagen rota), cae al `fallbackIcon` de la columna (o al ícono de imagen por defecto).
 */
function ThumbnailCell({
  src,
  alt,
  fallbackIcon,
}: {
  src?: string;
  alt?: string;
  fallbackIcon?: typeof ImageIcon;
}) {
  const [errored, setErrored] = useState(false);
  if (!src || errored) {
    return (
      <span className="ListView-imgPlaceholder" title="Sin imagen">
        <Icon source={fallbackIcon ?? ImageIcon} tone="subdued" />
      </span>
    );
  }
  return (
    <img
      src={src}
      alt={alt ?? ''}
      onError={() => setErrored(true)}
      loading="lazy"
      style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 8, display: 'block' }}
    />
  );
}

/** Encabezado estándar de columna en modo editar columnas: título + botón ojo. */
function defaultRenderColumnHeader(
  columnKey: string,
  column: ListViewColumn,
  isVisible: boolean,
  onToggleVisibility: () => void
) {
  return (
    <InlineStack gap="300" blockAlign="center" align="space-between">
      <Text
        as="span"
        variant="bodyMd"
        fontWeight={isVisible ? 'medium' : 'regular'}
        tone={isVisible ? undefined : 'subdued'}
      >
        {column.title || columnKey}
      </Text>
      <Box as="span" onPointerDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
        <Button
          icon={isVisible ? ViewIcon : HideIcon}
          variant="plain"
          size="slim"
          tone={isVisible ? undefined : 'subdued'}
          accessibilityLabel={isVisible ? 'Ocultar columna' : 'Mostrar columna'}
          onClick={onToggleVisibility}
        />
      </Box>
    </InlineStack>
  );
}

/**
 * Props del componente ListView.
 * @template Item - Tipo de cada fila; debe extender Record<string, unknown> (objeto con keys accesibles).
 * Especificar Item mejora el tipado de onRowEdit, onRowDelete, onRenderActions y getRowDisabled.
 */
/**
 * Agrupación opcional estilo Odoo (subheaders + subtotales colapsables) sobre el MISMO
 * IndexTable: conserva checkbox, clic de fila y popovers de las filas de datos. Aditiva:
 * si no se pasa `grouping` (o `levels` vacío), la tabla se renderiza igual que siempre.
 *
 * MULTINIVEL/acumulativa: `levels` es una lista ordenada de dimensiones; agrupa de forma
 * anidada (p. ej. [Mes, Día, Vendedor] → Mes › Día › Vendedor › filas), con subtotales y
 * colapso en cada nivel.
 */
export interface ListViewGroupLevel<Item extends object = Record<string, unknown>> {
  /** Grupo de cada item en este nivel (key estable + label visible). */
  groupOf: (item: Item) => { key: string; label: string };
  /** Orden de los nodos de este nivel. */
  compare?: (a: { key: string; label: string; items: Item[] }, b: { key: string; label: string; items: Item[] }) => number;
}
export interface ListViewGrouping<Item extends object = Record<string, unknown>> {
  /** Niveles de agrupación anidados (orden = profundidad). Vacío = sin agrupar. */
  levels: ListViewGroupLevel<Item>[];
  /** Subtotal por columna en cada subheader (key de columna → nodo). Vacío si no aplica. */
  aggregateCell?: (columnKey: string, items: Item[]) => ReactNode;
}

/** Fila del plan de render agrupado (subheader de grupo en algún nivel, o fila de datos hoja). */
type GroupPlanRow<I> =
  | { kind: 'group'; level: number; path: string; label: string; items: I[]; expanded: boolean; pos: number; leafRange: [number, number] | null }
  | { kind: 'data'; level: number; item: I; pos: number };

/** Medidas (subtotales) de un nodo de agrupación server-side. */
export interface ServerGroupMeasures { total: number; paid: number; due: number }
/** Fila del plan de agrupación SERVER-SIDE, ya armado por el consumidor (Ventas 2 a escala). */
export type ServerGroupPlanRow<Item extends object = Record<string, unknown>> =
  | { kind: 'group'; level: number; path: string; label: string; count: number; measures: ServerGroupMeasures; expanded: boolean; loading?: boolean }
  | { kind: 'data'; item: Item }
  | { kind: 'skeleton'; level: number }
  | { kind: 'note'; level: number; text: string };

export interface ListViewProps<Item extends object = Record<string, unknown>> {
  config: ListViewConfig<Item>;
  items: Item[];
  /** Si se define, las filas se agrupan con subheaders + subtotales (estilo Odoo). */
  grouping?: ListViewGrouping<Item>;
  /** Agrupación SERVER-SIDE (Ventas 2 a escala): el consumidor arma el plan (subheaders con
   *  subtotales EXACTOS de la BD + filas cargadas lazy) y lo pasa ya listo; ListView solo lo
   *  renderiza. Tiene prioridad sobre `grouping` (client-side); desactiva selección/paginación interna. */
  serverGroupPlan?: ServerGroupPlanRow<Item>[];
  /** Celda de subtotal de un grupo server (por columna) desde las medidas del nodo. */
  serverGroupAggregate?: (columnKey: string, measures: ServerGroupMeasures) => ReactNode;
  /** Expandir/colapsar un grupo server (el consumidor maneja el lazy load). */
  onToggleServerGroup?: (path: string) => void;
  /** Prefetch on-hover: se llama al pasar el mouse por un grupo → trae su detalle antes del clic. */
  onHoverServerGroup?: (path: string) => void;
  loading?: boolean;
  /** Mensaje bajo el spinner en estado loading (opcional). */
  loadingMessage?: string;
  /** Título de la página en estado loading (opcional; si no se pasa, se usa config.title / config.titleIcon). */
  pageTitle?: ReactNode;
  selectedIds?: string[];
  onSelectionChange?: (selectedIds: string[]) => void;
  onRowEdit?: (item: Item) => void;
  onRowDelete?: (item: Item) => void;
  onBulkAction?: (actionKey: string, selectedIds: string[]) => void;
  /**
   * Borrado masivo genérico de la plantilla. Si se define (y `can !== false`), la tabla
   * inyecta una acción bulk destructiva "Eliminar" con modal de confirmación + toast;
   * el borrado real (tenant-safe) lo aporta la página en `onDelete`. Ver ListViewBulkDelete.
   */
  bulkDelete?: ListViewBulkDelete;
  /** Tabs opcionales (ej. Todos / Activos / Borradores). El contenido de la tabla es el mismo; los datos los controla el padre. */
  tabs?: ListViewTabsOption;
  /** Si se define, cuando devuelve algo distinto de null se muestra en lugar del cuerpo por defecto (ej. tab "Crear"). */
  renderTabContent?: (selectedIndex: number) => ReactNode | null;
  /** Paginación opcional debajo de la tabla. */
  pagination?: ListViewPaginationOption;
  /** Acciones secundarias del Page (ej. Exportar, Importar, Más acciones).
   *  Acepta array de acciones estándar O ReactNode para controles custom
   *  (view switcher, segmented buttons, etc.). */
  secondaryActions?: { content: string; onAction?: () => void; actions?: { content: string; onAction: () => void }[] }[] | ReactNode;
  /**
   * Si se define, se renderiza en una Card propia encima de la lista (ej. bloque de KPIs).
   * Regla: la lista (filtros + tabla) va siempre en su propio contenedor; si hay bloque de KPIs, cada uno en su Card.
   * Si contentAboveCardInCard es false, no se envuelve en Card (sin fondo blanco exterior).
   */
  contentAboveCard?: ReactNode;
  /** Si false, contentAboveCard se renderiza sin Card envolvente (sin fondo blanco por fuera). Por defecto true. */
  contentAboveCardInCard?: boolean;
  /** Contenido opcional entre el Page y la tabla (ej. barra de filtros/vistas). Si hay contentAboveCard, aquí solo la barra de filtros. */
  contentBefore?: ReactNode;
  /**
   * Si true y hay contentBefore, la barra y la tabla se renderizan dentro del mismo Card (sin separación),
   * como en Shopify: un único contenedor blanco que incluye vistas/filtros y lista.
   */
  contentBeforeInsideCard?: boolean;
  /** Si false, la tabla no muestra checkboxes ni selección (ej. lista de pedidos solo lectura). */
  selectable?: boolean;
  /** Renderizado custom de la celda de acciones por fila (si se define, sustituye los botones Editar/Eliminar). */
  onRenderActions?: (item: Item) => ReactNode;
  /**
   * Click en TODA la fila → navega/acción de detalle (paridad Shopify), SIN togglear el checkbox.
   * Prioritario sobre onRowEdit/editRoute. Úsalo en páginas con navegación custom o detalle propio
   * (ops, clientes, pedidos online). El checkbox sigue seleccionando (su celda hace stopPropagation).
   */
  onRowClick?: (item: Item) => void;
  /** Empty state override (si se define, se usa en lugar de config.emptyState). */
  emptyState?: ListViewEmptyState;
  /** Si se define, las filas con true no son seleccionables y se muestran atenuadas (ej. roles de sistema). */
  getRowDisabled?: (item: Item) => boolean;
  /** Orden de columnas (keys). Si no se define, se usa el de config.columns. */
  columnOrder?: string[];
  /** Keys de columnas ocultas. Si no se define, todas son visibles. */
  hiddenColumnKeys?: string[];
  /** Si true, oculta la fila de encabezados de la tabla (ej. modo editar columnas inline). */
  hideTableHeader?: boolean;
  /**
   * Modo editar columnas: tabla como columnas completas (encabezado + datos) ordenables y scroll horizontal.
   * Requiere columnOrderForEdit, onColumnOrderChange y onToggleColumnVisibility; renderColumnHeader es opcional (hay default).
   */
  editColumnsMode?: boolean;
  /** Orden de keys de columnas en modo edición (todas, incl. ocultas). */
  columnOrderForEdit?: string[];
  /** Callback al reordenar columnas en modo edición. */
  onColumnOrderChange?: (newOrder: string[]) => void;
  /** Render del encabezado (pill con título + ojo). El arrastre se aplica a todo el contenedor de la columna. */
  renderColumnHeader?: (
    columnKey: string,
    column: ListViewColumn,
    isVisible: boolean,
    onToggleVisibility: () => void
  ) => ReactNode;
  /** Callback al alternar visibilidad de una columna en modo edición. */
  onToggleColumnVisibility?: (columnKey: string) => void;
  /** Si true, no se envuelve en Page (solo el contenido: filtros + tabla). Útil para embeber dentro de una pestaña. */
  embedded?: boolean;
  /** Ancho ampliado de la página (índice Shopify): usa Page fullWidth + regla max-width 1400px
   *  centrado (ver AppPage.fullWidth). Para listas densas en pantallas anchas. */
  fullWidth?: boolean;
  /**
   * Máximo de filas a mostrar en modo "Editar columnas" (reordenar/ocultar columnas).
   * Por defecto LIST_VIEW_EDIT_MODE_MAX_ROWS (50); aumentar puede degradar rendimiento.
   */
  editColumnsModeMaxRows?: number;
}

/**
 * Máximo de filas a renderizar en modo "Editar columnas" para evitar bloquear la UI (drag de columnas).
 * Se puede sobrescribir con la prop `editColumnsModeMaxRows` del ListView.
 */
export const LIST_VIEW_EDIT_MODE_MAX_ROWS = 50;

function getItemId<Item>(item: Item, idKey: string): string {
  const value = (item as Record<string, unknown>)[idKey];
  return value != null ? String(value) : '';
}

/** Bloque sortable: encabezado + todas las celdas de la columna (mueve columna completa). */
function SortableColumnBlock<Item>({
  id,
  column,
  isVisible,
  onToggleVisibility,
  renderColumnHeader,
  items,
  renderCellContent,
  idKey,
  dragWidth,
  registerColumnWidth,
}: {
  id: string;
  column: ListViewColumn;
  isVisible: boolean;
  onToggleVisibility: () => void;
  renderColumnHeader: (
    columnKey: string,
    col: ListViewColumn,
    visible: boolean,
    onToggle: () => void
  ) => ReactNode;
  items: Item[];
  renderCellContent: (item: Item) => ReactNode;
  idKey: string;
  dragWidth?: number | null;
  registerColumnWidth?: (id: string, width: number) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const borderColor = 'var(--p-color-border-secondary, #e1e3e5)';
  const fixedWidthWhileDragging = isDragging && dragWidth != null ? dragWidth : undefined;
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const rafIdRef = useRef<number | null>(null);
  const setRef = useCallback(
    (el: HTMLDivElement | null) => {
      setNodeRef(el);
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
      if (rafIdRef.current != null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      if (!el || !registerColumnWidth) return;
      // Diferir medición y ResizeObserver al siguiente frame para no bloquear el primer paint (evita lag al entrar en modo editar columnas)
      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = null;
        const reportWidth = () => {
          const w = el.offsetWidth;
          if (w > 0) registerColumnWidth(id, w);
        };
        reportWidth();
        const ro = new ResizeObserver(reportWidth);
        ro.observe(el);
        resizeObserverRef.current = ro;
      });
    },
    [setNodeRef, registerColumnWidth, id]
  );
  React.useEffect(
    () => () => {
      resizeObserverRef.current?.disconnect();
      if (rafIdRef.current != null) cancelAnimationFrame(rafIdRef.current);
    },
    []
  );
  return (
    <Box
      as="div"
      ref={setRef}
      data-sortable-column-id={id}
      {...attributes}
      {...listeners}
      style={{
        minWidth: 180,
        width: fixedWidthWhileDragging != null ? fixedWidthWhileDragging : 'max-content',
        ...(fixedWidthWhileDragging != null && { maxWidth: fixedWidthWhileDragging }),
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.85 : isVisible ? 1 : 0.55,
        zIndex: isDragging ? 1 : 0,
        cursor: 'grab',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor,
        borderRadius: 'var(--p-border-radius-200, 8px)',
        overflow: 'hidden',
      }}
    >
      <Box
        padding="300"
        borderBlockEndWidth="025"
        borderColor="border"
        background="bg-surface-secondary"
        minHeight={40}
      >
        {renderColumnHeader(id, column, isVisible, onToggleVisibility)}
      </Box>
      {items.map((item, index) => (
        <Box
          key={getItemId(item, idKey)}
          padding="300"
          borderBlockEndWidth={index < items.length - 1 ? '025' : '0'}
          borderColor="border"
          background="bg-surface"
          minHeight={44}
        >
          {renderCellContent(item)}
        </Box>
      ))}
    </Box>
  );
}

function renderCell<Item>(
  column: ListViewColumn,
  item: Item,
  /** Navegación del router. Se recibe por parámetro porque esto NO es un componente. */
  irA: (path: string) => void,
  onRowEdit?: (item: Item) => void,
  onRowDelete?: (item: Item) => void,
  onRenderActions?: (item: Item) => ReactNode
): ReactNode {
  const record = item as Record<string, unknown>;
  const value = record[column.key];

  switch (column.type) {
    case 'thumbnail': {
      const src = (column.imageKey ? record[column.imageKey] : value) as string | undefined;
      const alt = (column.altKey ? record[column.altKey] : record.name) as string | undefined;
      return <ThumbnailCell src={src} alt={alt} />;
    }
    case 'thumbnailWithText': {
      const imgKey = column.imageKey ?? column.key;
      const textKey = column.textKey ?? 'name';
      const src = (record[imgKey] as string) || '/placeholder.svg';
      const alt = (column.altKey ? record[column.altKey] : record.name) as string | undefined;
      const text = record[textKey] != null ? String(record[textKey]) : '—';
      const fontWeight = column.fontWeight ?? 'medium';
      return (
        <InlineStack gap="300" align="start">
          <Thumbnail source={src} alt={alt ?? ''} size="small" />
          <Text variant="bodyMd" fontWeight={fontWeight} as="span">
            {text}
          </Text>
        </InlineStack>
      );
    }
    case 'avatarWithText': {
      const textKey = column.textKey ?? column.key;
      const text = record[textKey] != null ? String(record[textKey]) : '—';
      const subtitle = column.subtitleKey && record[column.subtitleKey] != null
        ? String(record[column.subtitleKey])
        : null;
      const src = column.imageKey ? (record[column.imageKey] as string | null) : null;
      const alt = column.altKey ? String(record[column.altKey] ?? '') : text;
      // Iniciales determinísticas desde el texto principal — dos primeras
      // letras de las primeras dos palabras (o 2 primeras del nombre único).
      const initials = (() => {
        const parts = text.trim().split(/\s+/).filter(Boolean);
        if (parts.length === 0) return '?';
        if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
        return parts[0].substring(0, 2).toUpperCase();
      })();
      const fontWeight = column.fontWeight ?? 'medium';
      return (
        <InlineStack gap="300" blockAlign="center" wrap={false}>
          <Avatar
            size="md"
            name={text}
            initials={initials}
            source={src ?? undefined}
            accessibilityLabel={alt}
          />
          <BlockStack gap="050">
            <Text variant="bodyMd" fontWeight={fontWeight} as="span">
              {text}
            </Text>
            {subtitle && (
              <Text variant="bodySm" tone="subdued" as="span">
                {subtitle}
              </Text>
            )}
          </BlockStack>
        </InlineStack>
      );
    }
    case 'text': {
      const text = value != null ? String(value) : '—';
      const maxWidth = (column as ListViewColumnText).maxWidth ?? DEFAULT_COLUMN_MAX_WIDTH;
      const content = (
        <Text variant="bodyMd" fontWeight={column.fontWeight} tone={column.tone} as="span">
          {text}
        </Text>
      );
      return wrapCellWithMaxWidth(content, maxWidth, text);
    }
    case 'number': {
      const num = typeof value === 'number' ? value : Number(value);
      const safeNum = Number.isNaN(num) ? 0 : num;
      const formatted = column.format
        ? column.format(safeNum, record)
        : `${safeNum}${column.suffix ?? ''}`;
      const tone = column.toneFromValue ? column.toneFromValue(safeNum, record) : 'subdued';
      const content = (
        <Text variant="bodyMd" tone={tone} as="span">
          {formatted}
        </Text>
      );
      return wrapCellWithMaxWidth(content, DEFAULT_COLUMN_MAX_WIDTH, String(formatted));
    }
    case 'price': {
      const prefix = column.currencyPrefix ?? 'S/ ';
      // null/undefined/'' → sin precio: se muestra '—' (no 'S/ 0.00'). Distingue "sin precio de
      // comparación" de "vale cero".
      const hasValue = value != null && value !== '';
      const priceNum = hasValue ? Number(value) : NaN;
      const text = !Number.isNaN(priceNum) ? `${prefix}${priceNum.toFixed(2)}` : '—';
      const content = (
        <Text
          variant="bodyMd"
          fontWeight={column.fontWeight === 'regular' ? undefined : (column.fontWeight ?? 'medium')}
          tone={column.tone}
          as="span"
        >
          {text}
        </Text>
      );
      return wrapCellWithMaxWidth(content, DEFAULT_COLUMN_MAX_WIDTH, text);
    }
    case 'badge': {
      const label = column.format
        ? column.format(value, record)
        : (column.map?.[String(value)] ?? String(value ?? '—'));
      if (label === '' || label == null) return null;
      const tone = column.toneFromValue
        ? column.toneFromValue(value)
        : column.toneKey
          ? (record[column.toneKey] as 'success' | 'subdued' | 'attention' | undefined)
          : undefined;
      const content = (
        <Badge tone={tone ?? (value === true ? 'success' : 'subdued')}>
          {label}
        </Badge>
      );
      return wrapCellWithMaxWidth(content, DEFAULT_COLUMN_MAX_WIDTH, String(label));
    }
    case 'custom': {
      const rendered = column.render(record);
      if (column.maxWidth) {
        return wrapCellWithMaxWidth(rendered, column.maxWidth);
      }
      return rendered;
    }
    case 'actions': {
      if (onRenderActions) {
        // Wrapper de ancho completo con justify-content: center → el grupo
        // de acciones se centra dentro de la celda. Así filas con más o menos
        // botones (por permisos o estado) se ven balanceadas visualmente,
        // no anclan al borde.
        return (
          <div
            className="ListView-actionsCell"
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            {onRenderActions(item)}
          </div>
        );
      }
      const id = getItemId(item, 'id');
      const slug = (record.slug as string | undefined);
      let editUrl = '';
      if (column.editRoute) {
        if (column.editRoute.includes(':slug') && slug != null) {
          editUrl = column.editRoute.replace(':slug', encodeURIComponent(String(slug)));
        } else if (column.editRoute.includes(':id') && id) {
          editUrl = column.editRoute.replace(':id', id);
        }
      }
      const showEdit = Boolean(column.editRoute || onRowEdit);
      const editLabel = column.editLabel ?? 'Editar';
      const deleteLabel = column.deleteLabel ?? 'Eliminar';
      return (
        <div
          className="ListView-actionsCell"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <InlineStack gap="200">
            {showEdit && (
              <Tooltip content={editLabel}>
                <Button
                  icon={EditIcon}
                  variant="tertiary"
                  size="slim"
                  accessibilityLabel={editLabel}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onRowEdit) onRowEdit(item);
                    else irA(editUrl);
                  }}
                />
              </Tooltip>
            )}
            {column.delete && onRowDelete && (
              <Tooltip content={deleteLabel}>
                <Button
                  icon={DeleteIcon}
                  variant="tertiary"
                  tone="critical"
                  size="slim"
                  accessibilityLabel={deleteLabel}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRowDelete(item);
                  }}
                />
              </Tooltip>
            )}
          </InlineStack>
        </div>
      );
    }
    default: {
      const text = value != null ? String(value) : '—';
      const content = (
        <Text variant="bodyMd" as="span">
          {text}
        </Text>
      );
      return wrapCellWithMaxWidth(content, DEFAULT_COLUMN_MAX_WIDTH, text);
    }
  }
}

export function ListView<Item extends object = Record<string, unknown>>({
  config,
  items,
  grouping,
  serverGroupPlan,
  serverGroupAggregate,
  onToggleServerGroup,
  onHoverServerGroup,
  loading = false,
  loadingMessage = 'Cargando…',
  pageTitle,
  selectedIds = [],
  onSelectionChange,
  onRowEdit,
  onRowDelete,
  onBulkAction,
  bulkDelete,
  tabs,
  renderTabContent,
  pagination,
  secondaryActions,
  contentAboveCard,
  contentAboveCardInCard = true,
  contentBefore,
  contentBeforeInsideCard = false,
  selectable = true,
  onRenderActions,
  onRowClick,
  emptyState: emptyStateOverride,
  getRowDisabled,
  columnOrder: columnOrderProp,
  hiddenColumnKeys,
  hideTableHeader = false,
  editColumnsMode = false,
  columnOrderForEdit,
  onColumnOrderChange,
  renderColumnHeader,
  onToggleColumnVisibility,
  embedded = false,
  fullWidth = false,
  editColumnsModeMaxRows = LIST_VIEW_EDIT_MODE_MAX_ROWS,
}: ListViewProps<Item>) {
  /**
   * 🚨 Navegación por el ROUTER, no por el navegador.
   *
   * Esto usaba `window.location.href` en cuatro lugares —incluido el clic de fila, que es la acción
   * más usada del panel—. `window.location` no es «navegar»: tira la aplicación entera y la levanta
   * de cero. Cada clic en una lista volvía a descargar y parsear ~4,5 MB de JavaScript, re-montaba
   * React y re-autenticaba, antes de siquiera pedir los datos de la pantalla destino.
   *
   * Era el SPA apagado justo donde más se usa. Se sentía como «la lista tarda»; en realidad la
   * aplicación estaba arrancando de nuevo.
   *
   * 🪤 Va `useStoreNavigate` y no `useNavigate` a secas: las rutas del panel viven bajo
   * `store/:storeSlug` y los helpers de rutas devuelven el path SIN ese prefijo. Con `navigate()`
   * pelado se navegaría a una ruta que no existe en el árbol.
   */
  const storeNavigate = useStoreNavigate();
  const idKey = (config.idKey as string) ?? 'id';
  const editModeMaxRows = editColumnsModeMaxRows;
  const effectiveRenderColumnHeader = renderColumnHeader ?? defaultRenderColumnHeader;

  // Shell-first: no return temprano con solo spinner. Siempre se pinta la estructura (Page, filtros, Card)
  // y el loading solo se muestra dentro del cuerpo de la lista (plantilla carga+caché).
  const loadingContent = (
    <Box padding="800">
      <BlockStack gap="400" inlineAlign="center">
        <Spinner size="large" label={loadingMessage} />
        <Text as="p" variant="bodySm" tone="subdued">
          {loadingMessage}
        </Text>
      </BlockStack>
    </Box>
  );

  const effectiveColumns = useMemo(() => {
    const cols = config.columns;
    if (!columnOrderProp?.length && !hiddenColumnKeys?.length) return cols;
    const hidden = new Set(hiddenColumnKeys ?? []);
    const order = columnOrderProp ?? cols.map((c) => c.key);
    return order
      .filter((k) => !hidden.has(k))
      .map((k) => cols.find((c) => c.key === k))
      .filter(Boolean) as typeof cols;
  }, [config.columns, columnOrderProp, hiddenColumnKeys]);

  const editColumnsSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const [activeDragColumnId, setActiveDragColumnId] = useState<string | null>(null);
  const [activeDragColumnWidth, setActiveDragColumnWidth] = useState<number | null>(null);
  const [expandableOpen, setExpandableOpen] = useState<{ rowId: string; columnKey: string } | null>(null);
  const columnWidthsRef = useRef<Record<string, number>>({});
  const registerColumnWidth = useCallback((id: string, width: number) => {
    columnWidthsRef.current[id] = width;
  }, []);
  // Contenedor de scroll horizontal de la tabla → barra de scroll "pegajosa" (estilo Shopify).
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const selectableIds = useMemo(
    () => (getRowDisabled ? items.filter((i) => !getRowDisabled(i)).map((i) => getItemId(i, idKey)) : items.map((i) => getItemId(i, idKey))),
    [items, idKey, getRowDisabled]
  );

  const [internalSelectedIds, setInternalSelectedIds] = useState<string[]>([]);
  // Borrado masivo genérico de la plantilla: ids en confirmación pendiente (null = modal cerrado).
  const [pendingDeleteIds, setPendingDeleteIds] = useState<string[] | null>(null);
  /**
   * Consecuencias reales del borrado que está por confirmarse. Solo se consultan si la página las
   * aporta (`bulkDelete.consequences`); sin eso, el modal se comporta como siempre.
   */
  const [deleteConsequences, setDeleteConsequences] = useState<
    { description?: ReactNode; requireConfirmText?: string } | null
  >(null);
  const [deleteConsequencesLoading, setDeleteConsequencesLoading] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const toast = useAdminToastOptional();
  const isSelectionControlled = onSelectionChange != null;
  const effectiveSelectedIds = isSelectionControlled ? selectedIds : internalSelectedIds;
  const setEffectiveSelection = useCallback(
    (next: string[] | ((prev: string[]) => string[])) => {
      if (isSelectionControlled && onSelectionChange) {
        onSelectionChange(typeof next === 'function' ? next(selectedIds) : next);
      } else {
        setInternalSelectedIds(typeof next === 'function' ? next(internalSelectedIds) : next);
      }
    },
    [isSelectionControlled, onSelectionChange, selectedIds, internalSelectedIds]
  );

  // Poda selecciones "fantasma": si un item seleccionado desaparece de `items` (p. ej.
  // tras eliminar una fila), se quita de la selección para no dejar un "Seleccionados: N"
  // imposible de destildar. Corre cuando cambia `items`. Aplica a TODAS las listas.
  useEffect(() => {
    const present = new Set(items.map((i) => getItemId(i, idKey)));
    const stale = effectiveSelectedIds.some((id) => !present.has(id));
    if (stale) {
      setEffectiveSelection(effectiveSelectedIds.filter((id) => present.has(id)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // Grupos EXPANDIDos por path (default vacío = colapsados, estilo Odoo: ves totales y
  // expandes para drill-down). Multinivel: el path es 'k0¬k1¬k2…'.
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Modelo de agrupación multinivel: aplana el árbol (respetando colapso) a un plan de
  // filas con posición + rango de hojas por grupo, y un mapa posición→id (para que la
  // selección por rango sea correcta con subheaders intercalados). Sin niveles → plano.
  const groupModel = useMemo(() => {
    const levels = grouping?.levels ?? [];
    if (!grouping || levels.length === 0) {
      return { plan: null as GroupPlanRow<Item>[] | null, positionIds: items.map((i) => getItemId(i, idKey)) };
    }
    type Flat = { kind: 'group'; level: number; path: string; label: string; items: Item[]; expanded: boolean } | { kind: 'data'; level: number; item: Item };
    const flat: Flat[] = [];
    const walk = (rows: Item[], levelIdx: number, parentPath: string) => {
      if (levelIdx >= levels.length) {
        for (const it of rows) flat.push({ kind: 'data', level: levelIdx, item: it });
        return;
      }
      const map = new Map<string, { key: string; label: string; items: Item[] }>();
      const order: string[] = [];
      for (const it of rows) {
        const { key, label } = levels[levelIdx].groupOf(it);
        let g = map.get(key);
        if (!g) { g = { key, label, items: [] }; map.set(key, g); order.push(key); }
        g.items.push(it);
      }
      let groups = order.map((k) => map.get(k)!);
      const cmp = levels[levelIdx].compare;
      if (cmp) groups = groups.slice().sort(cmp);
      for (const g of groups) {
        const path = parentPath ? `${parentPath}¬${g.key}` : g.key;
        const expanded = expandedGroups.has(path);
        flat.push({ kind: 'group', level: levelIdx, path, label: g.label, items: g.items, expanded });
        if (expanded) walk(g.items, levelIdx + 1, path);
      }
    };
    walk(items, 0, '');
    const positionIds: string[] = [];
    const plan: GroupPlanRow<Item>[] = flat.map((r, pos) => {
      positionIds.push(r.kind === 'data' ? getItemId(r.item, idKey) : '');
      return r.kind === 'data'
        ? { kind: 'data', level: r.level, item: r.item, pos }
        : { kind: 'group', level: r.level, path: r.path, label: r.label, items: r.items, expanded: r.expanded, pos, leafRange: null };
    });
    // Rango de hojas (posiciones de filas data) por grupo, para selección de grupo.
    for (let i = 0; i < plan.length; i++) {
      const row = plan[i];
      if (row.kind !== 'group') continue;
      let first = -1, last = -1;
      for (let j = i + 1; j < plan.length; j++) {
        const d = plan[j];
        if (d.level <= row.level) break;
        if (d.kind === 'data') { if (first === -1) first = d.pos; last = d.pos; }
      }
      row.leafRange = first === -1 ? null : [first, last];
    }
    return { plan, positionIds };
  }, [grouping, items, expandedGroups, idKey]);

  const handleSelectionChange = useCallback(
    (selectionType: string, toggleType: boolean, selection: string | string[] | number[]) => {
      const current = effectiveSelectedIds;
      if (selectionType === 'single' && typeof selection === 'string') {
        if (getRowDisabled && items.some((i) => getItemId(i, idKey) === selection && getRowDisabled(i))) return;
        const next = toggleType
          ? (current.includes(selection) ? current.filter((id) => id !== selection) : [...current, selection])
          : current.filter((id) => id !== selection);
        setEffectiveSelection(next);
        return;
      }
      if (selectionType === 'range' && Array.isArray(selection) && selection.length === 2) {
        const [min, max] = selection as [number, number];
        // posiciones renderizadas → ids (vacío = subheader); soporta grouping y no-grouping.
        const idsInRange = groupModel.positionIds
          .slice(min, max + 1)
          .filter((id): id is string => !!id && (!getRowDisabled || !items.find((i) => getItemId(i, idKey) === id && getRowDisabled(i))));
        const nextSet = new Set(current);
        idsInRange.forEach((id) => (toggleType ? nextSet.add(id) : nextSet.delete(id)));
        setEffectiveSelection(Array.from(nextSet));
        return;
      }
      if (selectionType === 'page') {
        // Checkbox de cabecera / barra de selección. Modelo Gmail/Odoo: si YA hay
        // algo seleccionado (parcial o todo), un click deselecciona TODO. Solo si
        // no había nada, selecciona toda la página. (Polaris por defecto, en estado
        // indeterminado, dispara toggleType=true = "completar"; lo ignoramos a
        // propósito para que un único click siempre limpie.)
        const pageIds = selectableIds;
        const pageSet = new Set(pageIds);
        const hasAnyOnPage = current.some((id) => pageSet.has(id));
        setEffectiveSelection(
          hasAnyOnPage
            ? current.filter((id) => !pageSet.has(id))
            : Array.from(new Set([...current, ...pageIds]))
        );
        return;
      }
      if (selectionType === 'all') {
        // Acción explícita "Seleccionar todos los N": respeta el toggle de Polaris.
        const pageIds = selectableIds;
        if (toggleType) {
          setEffectiveSelection(Array.from(new Set([...current, ...pageIds])));
        } else {
          const pageSet = new Set(pageIds);
          setEffectiveSelection(current.filter((id) => !pageSet.has(id)));
        }
        return;
      }
      if (!selection) setEffectiveSelection([]);
    },
    [effectiveSelectedIds, setEffectiveSelection, items, idKey, getRowDisabled, selectableIds, groupModel]
  );

  const hasBulkActions = Boolean(config.bulkActions?.length && onBulkAction);
  // Borrado masivo lo habilita la página vía prop `bulkDelete` (con su propio gate de permiso).
  const bulkDeleteEnabled = Boolean(bulkDelete && bulkDelete.can !== false);
  // La columna de checkboxes aparece si hay acciones bulk O borrado masivo habilitado.
  const effectiveSelectable = selectable !== false && (hasBulkActions || bulkDeleteEnabled);

  const promotedBulkActions = useMemo(() => {
    const result: { content: string; destructive?: boolean; onAction: () => void }[] = [];
    if (config.bulkActions?.length && onBulkAction) {
      for (const ba of config.bulkActions) {
        result.push({
          content: ba.content,
          destructive: ba.destructive,
          onAction: () => {
            onBulkAction(ba.actionKey, effectiveSelectedIds);
            if (!isSelectionControlled) setInternalSelectedIds([]);
          },
        });
      }
    }
    // Acción destructiva "Eliminar" inyectada por la plantilla → abre el modal de confirmación.
    if (bulkDeleteEnabled) {
      result.push({
        content: bulkDelete!.label ?? 'Eliminar',
        destructive: true,
        onAction: () => {
          setPendingDeleteIds(effectiveSelectedIds);
          setDeleteConfirmText('');
          setDeleteConsequences(null);
          if (!bulkDelete?.consequences) return;
          setDeleteConsequencesLoading(true);
          void bulkDelete.consequences(effectiveSelectedIds)
            .then(setDeleteConsequences)
            /**
             * 🪤 Si no se pueden averiguar las consecuencias NO se sigue como si no hubiera: se
             * pide la palabra igual. Un error de red no puede volverse permiso para borrar sin
             * fricción — es el mismo criterio que en la ficha del producto.
             */
            .catch(() => setDeleteConsequences({ requireConfirmText: 'ELIMINAR' }))
            .finally(() => setDeleteConsequencesLoading(false));
        },
      });
    }
    return result;
  }, [config.bulkActions, onBulkAction, effectiveSelectedIds, isSelectionControlled, bulkDeleteEnabled, bulkDelete]);

  /** Texto estándar del botón "Agregar": "Agregar {recurso singular}". Regla de la plantilla. */
  const primaryAction = useMemo(() => {
    if (!config.primaryAction) return undefined;
    const defaultContent =
      config.resourceName?.singular != null
        ? `Agregar ${config.resourceName.singular}`
        : (config.primaryAction.content ?? '');
    const content = config.primaryAction.content ?? defaultContent;
    return {
      content,
      onAction:
        config.primaryAction.onAction ??
        (config.primaryAction.to ? () => { storeNavigate(config.primaryAction!.to!); } : undefined),
      icon: config.primaryAction.icon ?? PlusIcon,
    };
  }, [config.primaryAction, config.resourceName?.singular, storeNavigate]);
  const effectiveEmptyState = emptyStateOverride ?? config.emptyState;
  const { emptyAction, emptySecondaryAction } = useMemo(() => {
    const actionDef = effectiveEmptyState.action;
    const action = !actionDef
      ? undefined
      : actionDef.onAction
        ? { content: actionDef.content, onAction: actionDef.onAction }
        : actionDef.to
          ? { content: actionDef.content, onAction: () => { storeNavigate(actionDef.to!); } }
          : undefined;
    const secondary = effectiveEmptyState.secondaryAction?.onAction
      ? { content: effectiveEmptyState.secondaryAction.content, onAction: effectiveEmptyState.secondaryAction.onAction }
      : undefined;
    return { emptyAction: action, emptySecondaryAction: secondary };
  }, [effectiveEmptyState, storeNavigate]);

  const customTabContent = tabs && renderTabContent ? renderTabContent(tabs.selected) : null;

  // Mostrar paginación solo cuando hay más de una página (regla Shopify: solo para listas largas).
  const paginationBlock =
    pagination && items.length > 0 && pagination.total > 0 && pagination.total > pagination.pageSize ? (
      <Box padding="400" borderBlockStartWidth="025">
        <InlineStack align="space-between">
          <Text variant="bodyMd" tone="subdued" as="p">
            Mostrando {pagination.from}-{pagination.to} de {pagination.total} {config.resourceName.plural}
          </Text>
          <InlineStack gap="200" align="center">
            <Select
              label=""
              labelHidden
              options={pagination.pageSizeOptions ?? LIST_PAGE_SIZE_OPTIONS}
              value={String(pagination.pageSize)}
              onChange={(value) => pagination.onPageSizeChange(Number(value))}
            />
            <Pagination
              label={pagination.label ?? `Página ${pagination.totalPages}`}
              hasPrevious={pagination.hasPrevious}
              onPrevious={pagination.onPrevious}
              hasNext={pagination.hasNext}
              onNext={pagination.onNext}
            />
          </InlineStack>
        </InlineStack>
      </Box>
    ) : null;

  // Render de una fila de datos (reutilizable para modo plano y agrupado). Conserva
  // checkbox/selección, clic de fila → detalle y popovers (Cliente/Artículos).
  const renderDataRow = (item: Item, position: number, rowType: 'data' | 'child' = 'data'): ReactNode => {
    const id = getItemId(item, idKey);
    const isSelected = effectiveSelectable && effectiveSelectedIds.includes(id);
    const disabled = getRowDisabled?.(item) ?? false;
    const record = item as Record<string, unknown>;
    const handleRowClick = (() => {
      if (disabled) return undefined;
      if (onRowClick) return () => onRowClick(item);
      if (onRowEdit) return () => onRowEdit(item);
      const editRoute = effectiveColumns.find((c) => c.type === 'actions')?.editRoute;
      if (!editRoute) return undefined;
      const rowId = getItemId(item, 'id');
      const slug = record.slug as string | undefined;
      let url = editRoute;
      if (url.includes(':slug') && slug != null) url = url.replace(':slug', encodeURIComponent(String(slug)));
      else if (url.includes(':id') && rowId) url = url.replace(':id', rowId);
      if (url.includes(':')) return undefined;
      return () => { storeNavigate(url); };
    })();
    return (
      <AppTable.Row id={id} key={id} selected={isSelected} position={position} disabled={disabled} onClick={handleRowClick} rowType={rowType}>
        {effectiveColumns.map((col) => {
          const cellContent = renderCell(col, item, storeNavigate, onRowEdit, onRowDelete, onRenderActions);
          const isExpandable = hasExpandableContent(col);
          const isThisOpen = expandableOpen?.rowId === id && expandableOpen?.columnKey === col.key;
          return (
            <AppTable.Cell key={col.key}>
              {isExpandable ? (
                <ExpandableCellWrapper
                  item={record}
                  expandableContent={col.expandableContent}
                  isOpen={isThisOpen}
                  onToggle={() =>
                    setExpandableOpen((prev) =>
                      prev?.rowId === id && prev?.columnKey === col.key ? null : { rowId: id, columnKey: col.key }
                    )
                  }
                  onClose={() => setExpandableOpen(null)}
                >
                  {cellContent}
                </ExpandableCellWrapper>
              ) : (
                cellContent
              )}
            </AppTable.Cell>
          );
        })}
      </AppTable.Row>
    );
  };

  // Filas agrupadas MULTINIVEL desde el plan: subheaders anidados (indentados por nivel,
  // con chevron + label + conteo + subtotales) + filas de datos (child). selectionRange
  // hace que el checkbox del subheader seleccione todas las hojas del grupo.
  const groupedRowsMarkup: ReactNode[] = (() => {
    if (!grouping || !groupModel.plan) return [];
    // Rail/guía vertical por nivel (línea sutil, estilo sidebar) + sangría. Cada nivel
    // agrega una barra con borde izquierdo que, al apilarse, forma la línea del árbol.
    const rail = (level: number) =>
      level > 0
        ? Array.from({ length: level }, (_, i) => (
            <Box key={`r-${i}`} width="22px" minHeight="26px" borderInlineStartWidth="025" borderColor="border-secondary" />
          ))
        : null;
    return groupModel.plan.map((row) => {
      if (row.kind === 'data') return renderDataRow(row.item, row.pos, 'child');
      const isExpanded = row.expanded;
      const toggle = () =>
        setExpandedGroups((prev) => { const n = new Set(prev); if (n.has(row.path)) n.delete(row.path); else n.add(row.path); return n; });
      return (
        <AppTable.Row
          key={`grp-${row.path}`}
          id={`grp-${row.path}`}
          rowType="subheader"
          selectable={false}
          position={row.pos}
        >
          {effectiveColumns.map((col, ci) => (
            <AppTable.Cell key={col.key}>
              {ci === 0 ? (
                <InlineStack gap="100" blockAlign="center" wrap={false}>
                  {rail(row.level)}
                  <Button
                    variant="tertiary"
                    size="micro"
                    icon={isExpanded ? ChevronDownIcon : ChevronRightIcon}
                    onClick={toggle}
                    accessibilityLabel={isExpanded ? `Colapsar ${row.label}` : `Expandir ${row.label}`}
                  />
                  <Button variant="monochromePlain" onClick={toggle}>{row.label}</Button>
                  <Badge tone={row.level === 0 ? 'info' : undefined}>{String(row.items.length)}</Badge>
                </InlineStack>
              ) : (
                grouping.aggregateCell?.(col.key, row.items) ?? null
              )}
            </AppTable.Cell>
          ))}
        </AppTable.Row>
      );
    });
  })();

  // Agrupación SERVER-SIDE (Ventas 2 a escala): renderiza el plan ya armado por el consumidor
  // (subheaders con subtotales EXACTOS de la BD + filas cargadas lazy). Sin selección interna.
  const serverGroupedRowsMarkup: ReactNode[] = (serverGroupPlan ?? []).map((row, idx) => {
    if (row.kind === 'data') return renderDataRow(row.item, idx, 'child');
    if (row.kind === 'skeleton') {
      return (
        <AppTable.Row id={`sgk-${idx}`} key={`sgk-${idx}`} rowType="child" selectable={false} position={idx}>
          {effectiveColumns.map((col) => (
            <AppTable.Cell key={col.key}>
              {col.type === 'actions' ? null : <SkeletonBodyText lines={1} />}
            </AppTable.Cell>
          ))}
        </AppTable.Row>
      );
    }
    const railFor = (lvl: number) =>
      lvl > 1
        ? Array.from({ length: lvl - 1 }, (_, i) => (
            <Box key={`r-${i}`} width="22px" minHeight="26px" borderInlineStartWidth="025" borderColor="border-secondary" />
          ))
        : null;
    if (row.kind === 'note') {
      return (
        <AppTable.Row id={`sgn-${idx}`} key={`sgn-${idx}`} rowType="subheader" selectable={false} position={idx}>
          {effectiveColumns.map((col, ci) => (
            <AppTable.Cell key={col.key}>
              {ci === 0 ? (
                <InlineStack gap="100" blockAlign="center" wrap={false}>
                  {railFor(row.level + 1)}
                  <Text as="span" variant="bodySm" tone="subdued">{row.text}</Text>
                </InlineStack>
              ) : null}
            </AppTable.Cell>
          ))}
        </AppTable.Row>
      );
    }
    return (
      <AppTable.Row id={`sg-${row.path}`} key={`sg-${row.path}`} rowType="subheader" selectable={false} position={idx}>
        {effectiveColumns.map((col, ci) => (
          <AppTable.Cell key={col.key}>
            {ci === 0 ? (
              <div onMouseEnter={() => onHoverServerGroup?.(row.path)}>
              <InlineStack gap="100" blockAlign="center" wrap={false}>
                {railFor(row.level)}
                <Button variant="tertiary" size="micro" icon={row.expanded ? ChevronDownIcon : ChevronRightIcon} onClick={() => onToggleServerGroup?.(row.path)} accessibilityLabel={row.expanded ? `Colapsar ${row.label}` : `Expandir ${row.label}`} />
                <Button variant="monochromePlain" onClick={() => onToggleServerGroup?.(row.path)}>{row.label}</Button>
                <Badge tone={row.level === 1 ? 'info' : undefined}>{String(row.count)}</Badge>
                {row.loading ? <Spinner size="small" /> : null}
              </InlineStack>
              </div>
            ) : (
              serverGroupAggregate?.(col.key, row.measures) ?? null
            )}
          </AppTable.Cell>
        ))}
      </AppTable.Row>
    );
  });

  const innerContent = loading ? (
    loadingContent
  ) : customTabContent != null ? (
    customTabContent
  ) : (serverGroupPlan ? serverGroupPlan.length === 0 : items.length === 0) ? (
    <AppEmptyState
      heading={effectiveEmptyState.heading}
      description={effectiveEmptyState.description}
      image={effectiveEmptyState.image}
      action={emptyAction}
      secondaryAction={emptySecondaryAction}
    />
    ) : editColumnsMode &&
      columnOrderForEdit?.length &&
      effectiveRenderColumnHeader &&
      onColumnOrderChange &&
      onToggleColumnVisibility ? (
      <Box padding="0" background="bg-surface" paddingBlockStart="400">
        {items.length > editModeMaxRows ? (
          <Box padding="200" paddingBlockEnd="0">
            <Text as="p" variant="bodySm" tone="subdued">
              Mostrando {editModeMaxRows} de {items.length} filas para reordenar columnas.
            </Text>
          </Box>
        ) : null}
        <Box
          as="div"
          paddingBlockEnd="200"
          borderBlockStartWidth={0}
          borderInlineStartWidth="025"
          borderColor="border-secondary"
          style={{ borderInlineStartStyle: 'solid', overflowX: 'auto', overflowY: 'visible' }}
        >
          <DndContext
            sensors={editColumnsSensors}
            collisionDetection={closestCenter}
            onDragStart={(event: DragStartEvent) => {
              setActiveDragColumnId(String(event.active.id));
              const storedWidth = columnWidthsRef.current[String(event.active.id)];
              if (storedWidth != null) setActiveDragColumnWidth(storedWidth);
            }}
            onDragEnd={(event: DragEndEvent) => {
              setActiveDragColumnId(null);
              setActiveDragColumnWidth(null);
              const { active, over } = event;
              if (!over || active.id === over.id) return;
              const order = arrayMove(
                columnOrderForEdit,
                columnOrderForEdit.indexOf(active.id as string),
                columnOrderForEdit.indexOf(over.id as string)
              );
              onColumnOrderChange(order);
            }}
          >
            <SortableContext items={columnOrderForEdit} strategy={horizontalListSortingStrategy}>
              <Box
                as="div"
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  width: 'max-content',
                  minWidth: 'min-content',
                  gap: 'var(--p-space-300, 12px)',
                  paddingInline: 'var(--p-space-300, 12px)',
                }}
              >
                {columnOrderForEdit.map((key) => {
                  const col = config.columns.find((c) => c.key === key);
                  if (!col) return null;
                  const isVisible = !hiddenColumnKeys?.includes(key);
                  const itemsForEdit = items.slice(0, editModeMaxRows);
                  return (
                    <SortableColumnBlock
                      key={key}
                      id={key}
                      column={col}
                      isVisible={isVisible}
                      onToggleVisibility={() => onToggleColumnVisibility(key)}
                      renderColumnHeader={effectiveRenderColumnHeader}
                      items={itemsForEdit}
                      renderCellContent={(item) =>
                        renderCell(col, item, storeNavigate, onRowEdit, onRowDelete, onRenderActions)
                      }
                      idKey={idKey}
                      dragWidth={key === activeDragColumnId ? activeDragColumnWidth : null}
                      registerColumnWidth={registerColumnWidth}
                    />
                  );
                })}
              </Box>
            </SortableContext>
          </DndContext>
        </Box>
        {paginationBlock}
      </Box>
    ) : (
      <>
        <div
          ref={scrollContainerRef}
          className={`ListView-hscroll${hideTableHeader ? ' ListView-hideTableHeader' : ''}${grouping && groupModel.plan ? ' ListView-grouped' : ''}`}
          style={{ overflowX: 'auto', overflowY: 'hidden' }}
        >
          <Box as="div" style={{ minWidth: 'max-content' }}>
            <AppTable
              resourceName={config.resourceName}
              itemCount={serverGroupPlan ? serverGroupPlan.length : items.length}
              selectedIds={effectiveSelectable && !serverGroupPlan ? effectiveSelectedIds : []}
              onSelectionChange={effectiveSelectable && !serverGroupPlan ? handleSelectionChange : undefined}
              headings={hideTableHeader
                ? effectiveColumns.map(() => ({ title: '' }))
                : effectiveColumns.map((c) => (
                    c.type === 'actions'
                      ? { title: c.title, alignment: 'center' as const }
                      : { title: c.title }
                  ))}
              bulkActions={effectiveSelectable ? promotedBulkActions : []}
              selectable={effectiveSelectable && !serverGroupPlan}
            >
              {serverGroupPlan
                ? serverGroupedRowsMarkup
                : grouping && groupModel.plan
                  ? groupedRowsMarkup
                  : items.map((item, index) => renderDataRow(item, index))}
            </AppTable>
          </Box>
        </div>
        <StickyHorizontalScrollbar targetRef={scrollContainerRef} />
        {paginationBlock}
      </>
    );

  const tableOrEmptyOrLoading = (
    <Card padding="0">
      {innerContent}
    </Card>
  );

  const wrapWithTabs = useCallback(
    (content: ReactNode) =>
      tabs ? (
        <Tabs tabs={tabs.tabs} selected={tabs.selected} onSelect={tabs.onSelect}>
          {content}
        </Tabs>
      ) : (
        content
      ),
    [tabs]
  );

  const body = wrapWithTabs(tableOrEmptyOrLoading);

  const listCardContent =
    contentBefore && contentBeforeInsideCard ? (
      <Card padding="0">
        {contentBefore}
        {wrapWithTabs(innerContent)}
      </Card>
    ) : null;

  const listSection = listCardContent ?? (contentBefore ? (
    <BlockStack gap="400">
      {contentBefore}
      {body}
    </BlockStack>
  ) : body);

  const content = contentAboveCard ? (
    <BlockStack gap="400">
      {contentAboveCardInCard ? (
        <Card padding="0">{contentAboveCard}</Card>
      ) : (
        contentAboveCard
      )}
      {listSection}
    </BlockStack>
  ) : (
    listSection
  );

  const deleteCount = pendingDeleteIds?.length ?? 0;
  const deleteNoun = deleteCount === 1 ? config.resourceName.singular : config.resourceName.plural;

  // El modal de borrado masivo se monta en AMBOS returns (embedded y normal); si no,
  // en modo embedded (StandardListView) el clic en "Eliminar" no abriría nada.
  const bulkDeleteModal = bulkDeleteEnabled ? (
    <AppConfirmModal
      open={pendingDeleteIds !== null}
      onClose={() => { setPendingDeleteIds(null); setDeleteConfirmText(''); }}
      title={bulkDelete!.confirmTitle ?? `Eliminar ${deleteCount} ${deleteNoun}`}
      message={`¿Seguro que querés eliminar ${deleteCount} ${deleteNoun}?`}
      description={
        deleteConsequencesLoading ? (
          <Text as="p" variant="bodySm" tone="subdued">Revisando qué se pierde…</Text>
        ) : (
          <BlockStack gap="300">
            {deleteConsequences?.description ?? (
              <Text as="p" variant="bodySm" tone="subdued">Esta acción no se puede deshacer.</Text>
            )}
            {deleteConsequences?.requireConfirmText && (
              <TextField
                label={`Escribí "${deleteConsequences.requireConfirmText}" para confirmar`}
                value={deleteConfirmText}
                onChange={setDeleteConfirmText}
                autoComplete="off"
                placeholder={deleteConsequences.requireConfirmText}
              />
            )}
          </BlockStack>
        )
      }
      confirmLabel="Eliminar"
      destructive
      confirmDisabled={
        deleteConsequencesLoading ||
        (!!deleteConsequences?.requireConfirmText &&
          deleteConfirmText.trim().toLocaleLowerCase()
            !== deleteConsequences.requireConfirmText.trim().toLocaleLowerCase())
      }
      onConfirm={async () => {
        const ids = pendingDeleteIds ?? [];
        try {
          const n = await bulkDelete!.onDelete(ids);
          const count = typeof n === 'number' ? n : ids.length;
          const noun = count === 1 ? config.resourceName.singular : config.resourceName.plural;
          toast?.success(`${count === 1 ? 'Se eliminó' : 'Se eliminaron'} ${count} ${noun}`);
          if (isSelectionControlled) onSelectionChange?.([]);
          else setInternalSelectedIds([]);
          setPendingDeleteIds(null);
          setDeleteConfirmText('');
        } catch (e) {
          // Dejamos el modal abierto para reintentar o cancelar.
          toast?.error('No se pudo eliminar', e instanceof Error ? e.message : undefined);
        }
      }}
    />
  ) : null;

  if (embedded) {
    return (
      <>
        {content}
        {bulkDeleteModal}
      </>
    );
  }

  const titleNode =
    pageTitle ??
    (config.titleIcon ? (
      <PageBreadcrumbTitle
        icon={config.titleIcon}
        title={config.title}
        onBack={config.onBack}
        backAccessibilityLabel={config.backAccessibilityLabel}
      />
    ) : (
      config.title
    ));

  return (
    <>
      <AppPage
        title={titleNode}
        primaryAction={primaryAction}
        secondaryActions={secondaryActions}
        fullWidth={fullWidth}
      >
        {content}
      </AppPage>
      {bulkDeleteModal}
    </>
  );
}
